import React, { useMemo, useRef } from 'react';
import { Input, Modal } from 'antd';
import { Formik } from 'formik';
import { Actions } from 'pages/[tenant]/settings/groups/index';

import { addErrorMessage, DebounceSelect, FormField, FormikFieldTouched } from 'components/common';
import { CHARACTER_SIZE } from 'lib/global';
import useI18n from 'lib/hooks/useI18n';
import { nameVerify } from 'lib/regExp';
import { FormError } from 'lib/types/error';
import { getUserOptionsByName, getUserOptionsByValues } from 'lib/users';

export type AddValueProps = {
  name: string;
  key: string;
  users: string[];
};

interface UserGroupProps {
  visible: boolean;
  initialValues: AddValueProps;
  actionType: Actions;
  confirmLoading: boolean;
  onSubmit: (values: AddValueProps) => void;
  onCancel: () => void;
}

const AddeUserGroupModal: React.FC<UserGroupProps> = ({
  visible,
  initialValues,
  actionType,
  confirmLoading,
  onSubmit,
  onCancel,
}) => {
  const { t } = useI18n();
  const formRef = useRef(null);
  const isEdit = useMemo(() => {
    return actionType === Actions.Edit;
  }, [actionType]);

  const onValidate = async value => {
    const errors = {} as FormError;
    const name = value.name;
    const errorName = nameVerify(name);
    if (!name) {
      errors.name = addErrorMessage(errors.name, t('pages.groups.validate.name'));
    } else if (
      name.length < CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH ||
      name.length > CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH
    ) {
      errors.name = addErrorMessage(
        errors.name,
        t('validate.fields.NAME_LENGTH_ERROR', {
          min: CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH,
          max: CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH,
        }),
      );
    } else if (errorName) {
      errors.name = addErrorMessage(errors.name, errorName);
    }

    return errors;
  };

  return (
    <Formik innerRef={formRef} initialValues={initialValues} onSubmit={onSubmit} validate={onValidate}>
      {({ handleSubmit }) => (
        <Modal
          maskClosable={false}
          title={`${isEdit ? t('global.edit') : t('global.create')} ${t('global.userGroup')} `}
          open={visible}
          confirmLoading={confirmLoading}
          onOk={() => handleSubmit()}
          onCancel={onCancel}
          destroyOnClose
        >
          <FormikFieldTouched />
          <FormField name="name" label={t('global.name')} required>
            {({ field }) => (
              <Input
                maxLength={CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH}
                placeholder={t('pages.groups.default.placeholder.0')}
                {...field}
              />
            )}
          </FormField>
          <FormField name="key" label={t('global.description')}>
            {({ field }) => (
              <Input
                maxLength={CHARACTER_SIZE.MAX_SYSTEM_DESCRIPTION_SPECIAL_LENGTH}
                placeholder={t('pages.groups.default.placeholder.1')}
                {...field}
              />
            )}
          </FormField>
          <FormField name="users" label={t('pages.groups.default.users')}>
            {({ field }) => (
              <DebounceSelect
                mode="multiple"
                allowClear
                showSearch
                fetchOptions={getUserOptionsByName}
                fetchValues={getUserOptionsByValues}
                placeholder={t('pages.workflow.placeholder.user')}
                {...field}
              />
            )}
          </FormField>
        </Modal>
      )}
    </Formik>
  );
};

export default AddeUserGroupModal;
